// Errores de una pregunta del chat -> mensaje para el usuario y la acción
// que se le ofrece. El backend manda {error: <código>, reason?, message?}:
// por WebSocket (AgentSocket) y por HTTP (InvokeAgentAPI). Cuando
// credits-engine rechaza la reserva, agent-runtime responde
// error="insufficient_credits" con el motivo real en `reason` (puede ser un
// límite de usuario o de cuenta, no solo falta de saldo).

const CREDIT_REASONS = {
    INSUFFICIENT_CREDITS: {
        message: 'La cuenta se quedó sin créditos para responder esta pregunta. '
            + 'Un administrador puede recargar créditos o mejorar el plan.',
        action: 'plans'
    },
    USER_CREDIT_LIMIT_EXCEEDED: {
        message: 'Llegaste al límite de créditos asignado a tu usuario (diario o mensual). '
            + 'Se renueva al empezar el siguiente periodo; si lo necesitas antes, pide a un '
            + 'administrador que lo amplíe en Política de crédito.'
    },
    ACCOUNT_CREDIT_LIMIT_EXCEEDED: {
        message: 'La cuenta llegó a su límite de créditos del día o del mes. Se renueva al '
            + 'empezar el siguiente periodo; un administrador puede ampliarlo o mejorar el plan.',
        action: 'plans'
    },
    OPERATION_LIMIT_EXCEEDED: {
        message: 'Esta pregunta necesita más créditos de los permitidos para una sola consulta. '
            + 'Prueba con una pregunta más acotada o con el modo normal.'
    },
    ACCOUNT_INACTIVE: {
        message: 'Tu cuenta está desactivada, por eso no puedo responder. Contacta a un administrador.'
    },
    PAYMENT_PAST_DUE: {
        message: 'La suscripción tiene un pago pendiente. Actualiza el método de pago para seguir preguntando.',
        action: 'plans'
    }
};

const GENERIC_MESSAGE = 'No pude responder esta pregunta por un problema del servicio. Inténtalo de nuevo en unos momentos.';

const BY_CODE = {
    plan_required: { action: 'plans' },
    no_data_connections: {
        message: 'Tu cuenta todavía no tiene ninguna fuente de datos conectada. Conecta una para poder preguntar.',
        action: 'connections'
    },
    model_not_available: {
        message: 'El modelo elegido ya no está disponible. Cambié al modelo del agente; vuelve a enviar tu pregunta.'
    },
    model_validation_unavailable: {},
    deep_analysis_disabled: {},
    dashboard_disabled: {},
    deep_analysis_requires_websocket: {},
    dashboard_requires_websocket: {},
    timeout: {
        message: 'La respuesta tardó más de lo esperado y dejé de esperarla. Vuelve a enviar la pregunta; '
            + 'si es muy amplia, prueba a acotarla.'
    },
    recovery_failed: {
        message: 'No se pudo recuperar la respuesta de esta pregunta. Vuelve a enviarla.'
    }
};

const retryText = (seconds) => {
    const value = Number(seconds);
    if (!Number.isFinite(value) || value <= 0) return 'un momento';
    if (value < 60) return `${Math.ceil(value)} segundos`;
    const minutes = Math.ceil(value / 60);
    return minutes === 1 ? '1 minuto' : `${minutes} minutos`;
};

/**
 * {code, reason, message, retryAfterSeconds} -> {message, action}.
 * `action`: 'plans' (abrir el modal de planes), 'connections' (ir a
 * conexiones) o null.
 */
export const questionErrorMessage = ({ code, reason, message, retryAfterSeconds } = {}) => {
    if (code === 'insufficient_credits') {
        const known = CREDIT_REASONS[reason] || CREDIT_REASONS.INSUFFICIENT_CREDITS;
        return { message: known.message, action: known.action || null };
    }
    if (code === 'rate_limited') {
        return {
            message: `Estás enviando preguntas muy seguido. Espera ${retryText(retryAfterSeconds)} y vuelve a intentarlo.`,
            action: null
        };
    }
    const known = BY_CODE[code];
    if (known) {
        return { message: known.message || message || GENERIC_MESSAGE, action: known.action || null };
    }
    // Código desconocido ("Internal server error", errores de red…): el
    // detalle técnico queda en la consola, al usuario un mensaje genérico.
    return { message: GENERIC_MESSAGE, action: null };
};

/**
 * Turno de error que se agrega a la conversación debajo de la pregunta (y
 * se guarda con ella). Tiene `text` para que el resto del chat lo trate
 * como una respuesta: así la pregunta ya no queda "sin responder".
 */
export const questionErrorTurn = (error, key) => {
    const { message, action } = questionErrorMessage(error);
    return {
        text: message,
        _key: key,
        error: { code: error?.code || null, reason: error?.reason || null, action }
    };
};
